// src/lib/resources/data/ebooks/email-model.ts

/**
 * @fileoverview Pure builder for the gated-resource Postmark template model used
 * by an ebook's lead-capture email.
 * @description Extracted from `EbookViewer` so the outbound model is unit-testable
 * — specifically that the email's visual preview (`resource_icon_url`, which the
 * `gated-resource-1` template renders as its only image) is the eBook's
 * per-title `images.og`, NOT the shared ebooks-hub hero. See ebook-email-model.test.ts.
 * @module resources/data/ebooks/email-model
 */

import { type Ebook } from '~/lib/resources/types/ebooks'
import { type RelatedResource } from '~/lib/resources/types/shared/base'

const DEFAULT_VALUES = {
  sender_name: 'The AdSights Team',
  product_name: 'AdSights',
  product_url: 'https://www.adsights.ai',
  logo_url: 'https://www.adsights.ai/logo-square.png',
  company_name: 'AdSights Inc.',
  company_address: '254 Chapman Rd, Ste 208 #28056, Newark, Delaware 19702',
  support_email: 'support@adsights.ai',
} as const

type DefaultValues = typeof DEFAULT_VALUES

/** Postmark `gated-resource-1` template model for an ebook lead-capture email. */
export interface EbookGatedEmailModel {
  base: DefaultValues
  gatedResource: DefaultValues & {
    preheader: string
    resource_icon_url: string
    resource_name: string
    resource_link: string
    resource_preview_link: string
    resource_share_url: string
    resource_description: string
    resource_type_label: string
    resource_cta_label: string
    resource_access_heading: string
    resource_delivery_note: string
    resource_usage_heading: string
    resource_usage_tip: string
    related_resource_1_link?: string
    related_resource_1_name?: string
    related_resource_2_link?: string
    related_resource_2_name?: string
    twitter_share_url: string
    linkedin_share_url: string
  }
  custom: {
    template_type: string
    ebook_id: string
    ebook_category?: string
    access_granted: boolean
  }
}

/**
 * The per-title image shown in the email + download modal. Prefers the eBook's
 * dedicated `images.og` (added per #216 follow-up); falls back to the download
 * config's `previewUrl` only if a title somehow has no OG image.
 *
 * @param {Ebook} ebook - The eBook resource.
 * @returns {string | undefined} Site-relative preview image path.
 */
export function ebookPreviewImageUrl(ebook: Ebook): string | undefined {
  return ebook.images?.og?.url ?? ebook.config?.download?.previewUrl
}

/**
 * Build the `gated-resource-1` Postmark template model for an ebook.
 *
 * @param {Ebook} ebook - The eBook resource.
 * @param {string} baseUrl - Absolute site origin (no trailing slash).
 * @returns {Record<string, unknown>} `{ base, gatedResource, custom }` template model.
 */
export function buildEbookGatedEmailModel(ebook: Ebook, baseUrl: string): EbookGatedEmailModel {
  // Mirror EbookViewerWrapper's on-page filter: a RelatedResource with no
  // `fullRoute` would render `${baseUrl}undefined`, so drop those here too —
  // otherwise the email and the on-page "related" rail disagree.
  const relatedResourceRefs =
    ebook.relatedResources?.filter(
      (r): r is RelatedResource => typeof r !== 'string' && Boolean(r.fullRoute),
    ) ?? []

  const previewPath = ebookPreviewImageUrl(ebook)
  // The template renders this 44×44 image as its only visual; keep it per-title.
  const previewUrlAbsolute = previewPath ? `${baseUrl}${previewPath}` : DEFAULT_VALUES.logo_url

  // Since #238 made eBooks email-only, this link IS the delivery mechanism — it
  // must never degrade to the bare origin. Prefer the configured download URL;
  // if a title is missing one, fall back to the eBook's own page (which always
  // exists and offers the download) rather than emitting a root-pointing button.
  const ebookPageUrl = `${baseUrl}/resources/ebooks/${ebook.slug}`
  const downloadPath = ebook.config?.download?.contentUrl
  const resourceLink = downloadPath ? `${baseUrl}${downloadPath}` : ebookPageUrl

  return {
    base: { ...DEFAULT_VALUES },
    gatedResource: {
      ...DEFAULT_VALUES,
      preheader: `Download ${ebook.title}`,
      resource_icon_url: previewUrlAbsolute,
      resource_name: ebook.title,
      resource_link: resourceLink,
      resource_preview_link: previewUrlAbsolute,
      resource_share_url: ebookPageUrl,
      resource_description: ebook.description,
      resource_type_label: 'Your ebook is ready',
      resource_cta_label: 'Download the ebook',
      resource_access_heading: 'Your ebook download link',
      resource_delivery_note:
        'Save this email so you can reopen the ebook, share it with your team, or revisit the companion resources later.',
      resource_usage_heading: 'Suggested next step',
      resource_usage_tip:
        'Open the ebook while the context is fresh, then bookmark the companion resources for your next planning session.',
      ...(relatedResourceRefs[0] && {
        related_resource_1_link: `${baseUrl}${relatedResourceRefs[0].fullRoute}`,
        related_resource_1_name: relatedResourceRefs[0].title,
      }),
      ...(relatedResourceRefs[1] && {
        related_resource_2_link: `${baseUrl}${relatedResourceRefs[1].fullRoute}`,
        related_resource_2_name: relatedResourceRefs[1].title,
      }),
      twitter_share_url: `https://twitter.com/intent/tweet?url=${encodeURIComponent(`${baseUrl}/resources/ebooks/${ebook.slug}`)}&text=${encodeURIComponent(`Check out this ${ebook.title} from @AdSightsAI`)}`,
      linkedin_share_url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(`${baseUrl}/resources/ebooks/${ebook.slug}`)}`,
    },
    custom: {
      template_type: 'ebook',
      ebook_id: ebook.id,
      ebook_category: ebook.category,
      access_granted: false,
    },
  }
}
